'use client'

import { useCallback, useEffect, useState, useMemo } from 'react'
import { clsx } from 'clsx'
import { Navigation, Mousewheel, Keyboard } from 'swiper/modules'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Icon } from '@/components/Icon'
import Popup from '@/components/Popup/Popup'
import { Title } from '@/components/Title'
import { Typography } from '@/components/Typography'
import styles from './IntegrationTVProgram.module.scss'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

type Program = {
  readonly titel: string
  readonly sendezeit: string
  readonly kurztext450: string
  readonly sendungsId: string
  readonly sendeschluss: string
}

export type WidgetIntegrationTvProgramProps = {
  readonly model?: {
    readonly fields?: {
      readonly headline: string
    }
    readonly json: {
      readonly ['TV-Programm']: {
        readonly [key: string]: {
          readonly sendung: Array<Program>
        }
      }
    }
  }
}

// Helper functions
const isToday = (dateString: string): boolean => {
  const givenDate = new Date(dateString)
  const today = new Date()
  return (
    givenDate.getFullYear() === today.getFullYear() &&
    givenDate.getMonth() === today.getMonth() &&
    givenDate.getDate() === today.getDate()
  )
}

const getProgramStatus = (sendezeit: string, sendeschluss: string) => {
  // Parse the date strings into Date objects
  const startTime = new Date(sendezeit)
  const endTime = new Date(sendeschluss)
  const currentTime = new Date() // Current time

  // Check if the program is happening now
  const isHappeningNow = currentTime >= startTime && currentTime <= endTime

  // Calculate percentage of program that has passed
  const totalDuration = endTime.getTime() - startTime.getTime() // Duration in milliseconds
  const elapsedTime = currentTime.getTime() - startTime.getTime() // Elapsed time in milliseconds

  // Calculate percentage if program is ongoing
  let percentage = 0
  if (isHappeningNow && totalDuration > 0) {
    percentage = (elapsedTime / totalDuration) * 100
  }

  return {
    isHappeningNow,
    percentage: Math.min(100, Math.max(0, percentage)), // Ensure percentage is between 0 and 100
  }
}

const formatTime = (dateString: string): string =>
  new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateString))

const getJetztFilter = () => {
  const now = new Date()
  return {
    name: 'Jetzt',
    start: `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`,
  }
}

const daytime_filters = [
  { name: 'Nachts', start: '00:00' },
  { name: 'Morgens', start: '06:00' },
  { name: 'Mittags', start: '12:00' },
  { name: 'Abends', start: '20:15' },
]

const IntegrationTvProgram = ({ item }: { item: WidgetIntegrationTvProgramProps }) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null)
  const [selectedOption, setSelectedOption] = useState<string | undefined>()
  const [selectedFilter, setSelectedFilter] = useState(daytime_filters[0])

  // Memoize program data to avoid unnecessary recalculation
  const programContext = useMemo(() => item.model?.json['TV-Programm'] ?? {}, [item.model?.json])
  const programKeys = useMemo(() => Object.keys(programContext), [programContext])

  // Set the default selected option to the first program when available
  useEffect(() => {
    if (programKeys.length > 0) {
      setSelectedOption(programKeys[0])
    }
  }, [programKeys])

  // Set selected filter based on current time and first program's date
  useEffect(() => {
    if (selectedOption && programContext[selectedOption]?.sendung?.length) {
      const firstProgram = programContext[selectedOption].sendung[0].sendezeit

      if (isToday(firstProgram)) {
        setSelectedFilter(getJetztFilter())
      } else {
        setSelectedFilter(daytime_filters[0]) // Default to "Morgens"
      }
    }
  }, [selectedOption, programContext])

  const handleFilterClick = useCallback(
    (filter: { name: string; start: string }) => {
      if (!selectedOption || !programContext[selectedOption]?.sendung?.length) return

      const schedule = programContext[selectedOption].sendung
      const [hours, minutes] = filter.start.split(':').map(Number)
      const targetTime = hours * 60 + minutes

      const index = schedule.findIndex(({ sendezeit }) => {
        const date = new Date(sendezeit)
        return date.getHours() * 60 + date.getMinutes() >= targetTime
      })

      if (index !== -1) {
        swiper?.slideTo(index - 1, 500)
        setSelectedFilter(filter)
      }
    },
    [selectedOption, swiper, programContext],
  )

  // Update the selected filter based on the active slide change
  useEffect(() => {
    if (!swiper || !selectedOption) return

    const handleSlideChange = () => {
      const activeIndex = swiper.activeIndex
      const schedule = programContext[selectedOption]?.sendung
      if (!schedule || schedule.length === 0) return

      const visibleProgram = schedule[activeIndex]
      if (!visibleProgram) return

      const visibleTime = formatTime(visibleProgram.sendezeit)
      const [hours, minutes] = visibleTime.split(':').map(Number)
      const totalMinutes = hours * 60 + minutes

      let newFilter =
        daytime_filters.find((filter, index) => {
          const nextFilter = daytime_filters[index + 1]
          const [filterHours, filterMinutes] = filter.start.split(':').map(Number)
          const filterMinutesTotal = filterHours * 60 + filterMinutes

          if (!nextFilter) return totalMinutes >= filterMinutesTotal // Last filter
          const [nextFilterHours, nextFilterMinutes] = nextFilter.start.split(':').map(Number)
          const nextFilterMinutesTotal = nextFilterHours * 60 + nextFilterMinutes

          return totalMinutes >= filterMinutesTotal && totalMinutes < nextFilterMinutesTotal
        }) || daytime_filters[0]

      // Ensure "Nachts" is selected ONLY for times between 00:00 - 06:59
      if (totalMinutes >= 0 && totalMinutes < 6 * 60) {
        newFilter = daytime_filters.find((f) => f.name === 'Nachts')!
      }

      if (newFilter.name !== selectedFilter.name) {
        setSelectedFilter(newFilter)
      }
    }

    swiper.on('slideChange', handleSlideChange)
    return () => swiper.off('slideChange', handleSlideChange)
  }, [swiper, selectedOption, programContext, selectedFilter])

  // Make sure the swiper slides to the correct filter on initial load
  useEffect(() => {
    if (swiper && selectedOption) {
      const schedule = programContext[selectedOption]?.sendung
      if (schedule && schedule.length > 0) {
        const firstProgram = schedule[0].sendezeit
        const firstFilter = isToday(firstProgram) ? getJetztFilter() : daytime_filters[0] // Default to "Morgens" if not today
        const targetTime = firstFilter.start

        const index = schedule.findIndex(({ sendezeit }) => {
          const programTime = formatTime(sendezeit)
          return programTime >= targetTime
        })

        if (index !== undefined && index !== -1) {
          swiper?.slideTo(index - 1, 500)
          setSelectedFilter(firstFilter)
        }
      }
    }
  }, [swiper, selectedOption, programContext])

  const [popupOpen, setPopupOpen] = useState<boolean>(false)
  const [currentProgram, setCurrentProgram] = useState<Program | null>(null)

  return (
    <section>
      <Title>{item.model?.fields?.headline}</Title>
      <div className={styles.nav}>
        <select value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)}>
          {programKeys.map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>

        <ul>
          {selectedOption && isToday(programContext[selectedOption]?.sendung?.[0]?.sendezeit ?? '') && (
            <li
              key="Jetzt"
              className={clsx({
                [styles.active]: selectedFilter.name === 'Jetzt',
              })}
            >
              <Typography onClick={() => handleFilterClick(getJetztFilter())} as="span" variant="s" weight="bold">
                Jetzt
              </Typography>
            </li>
          )}
          {daytime_filters.map((filter) => (
            <li
              key={filter.name}
              className={clsx({
                [styles.active]: filter.name === selectedFilter.name,
              })}
            >
              <Typography onClick={() => handleFilterClick(filter)} as="span" variant="s" weight="bold">
                {filter.name}
              </Typography>
            </li>
          ))}
        </ul>
      </div>

      <div style={{ width: '100%', overflow: 'hidden', marginTop: '24px' }}>
        <Swiper
          onSwiper={setSwiper}
          breakpoints={{
            768: { slidesPerView: 4.5, slidesPerGroup: 4 },
            1280: { slidesPerView: 5.5, slidesPerGroup: 5 },
            1440: { slidesPerView: 6.5, slidesPerGroup: 6 },
          }}
          className={styles.swiper}
          keyboard
          autoHeight
          modules={[Navigation, Mousewheel, Keyboard]}
          mousewheel
          navigation={{
            nextEl: `.image-swiper-button-next-tvProgramm`,
            prevEl: `.image-swiper-button-prev-tvProgramm`,
            disabledClass: 'swiper-button-disabled',
          }}
          pagination={{ el: `.pagination-tvProgramm`, type: 'fraction' }}
          slidesPerView={2.5}
          spaceBetween={4}
        >
          {selectedOption &&
            programContext[selectedOption]?.sendung?.map((program) => {
              const { isHappeningNow, percentage } = getProgramStatus(program.sendezeit, program.sendeschluss)
              return (
                <SwiperSlide
                  key={program.sendungsId}
                  className={styles['swiper-slider']}
                  title={program.titel}
                  onClick={() => {
                    setCurrentProgram(program)
                    setPopupOpen(true)
                  }}
                >
                  <Typography className={styles.title} weight="bold">
                    {program.titel}
                  </Typography>
                  <Typography variant="s">
                    {formatTime(program.sendezeit)}&nbsp;-&nbsp;
                    {formatTime(program.sendeschluss)}
                  </Typography>
                  {isHappeningNow && <progress className={styles.progress} value={percentage} max="100" />}
                </SwiperSlide>
              )
            })}
        </Swiper>

        {popupOpen && currentProgram && (
          <Popup title={currentProgram.titel} setIsOpen={setPopupOpen}>
            <Typography variant="m">{currentProgram.kurztext450}</Typography>
            <Typography variant="s">
              {formatTime(currentProgram.sendezeit)}&nbsp;-&nbsp;
              {formatTime(currentProgram.sendeschluss)}
            </Typography>
          </Popup>
        )}

        <div className={styles.controls}>
          <button className={`image-swiper-button-prev-tvProgramm ${styles.arrow}`}>
            <Icon name="chevronLeft" />
          </button>
          <button className={`image-swiper-button-next-tvProgramm ${styles.arrow}`}>
            <Icon name="chevronRight" />
          </button>
        </div>
      </div>
    </section>
  )
}

export default IntegrationTvProgram
